import { React, PT, fetchApi } from '@gasbuddy/react';
import { Button, Modal, Header, Form, Icon, Popup, Message } from 'semantic-ui-react';

function defaultState({ defaultValue }) {
  if (defaultValue) {
    return {
      payload: defaultValue.payload ? JSON.stringify(defaultValue.payload, null, '\t') : '',
      name: defaultValue.name,
      desirability: defaultValue.desirability,
      points: defaultValue.points,
      isControl: defaultValue.is_control_variant,
      ios_semver: defaultValue.ios_semver,
      android_semver: defaultValue.android_semver,
      other_semver: defaultValue.other_semver,
    };
  }
  return {};
}


export class AddFeatureVariant extends React.Component {
  state = defaultState(this.props);

  onChange = (f, v) => { this.setState({ [f]: v }); }

  saveVariant = () => {
    const { defaultValue, fetch: fetchFn, featureId } = this.props;
    const {
      name, remaining_assignments: remain, desirability, payload, isControl, points,
      ios_semver: ios, android_semver: android, other_semver: other,
    } = this.state;
    const variantId = (defaultValue && defaultValue.feature_variant_id) || 'add';
    this.setState({ loading: true, error: null }, async () => {
      const { status, body } = await fetchApi({
        url: `/system/features/${featureId}/variants/${variantId}`,
        body: {
          name,
          points: points || 0,
          is_control_variant: isControl,
          payload,
          desirability,
          remaining_assignments: 'remaining_assignments' in this.state ? remain : undefined,
          ios_semver: ios,
          android_semver: android,
          other_semver: other,
        },
      });
      if (status !== 200) {
        this.setState({ loading: false, error: body });
      } else {
        this.setState({
          loading: false,
          opened: false,
          name: variantId === 'add' ? null : name,
          remaining_assignments: undefined,
        });
        fetchFn();
      }
    });
  }

  render() {
    const { defaultValue, buttonLabel, buttonIcon, ignorePoints } = this.props;
    const {
      opened, error, payload, isControl, desirability,
      remaining_assignments: remain, points, name, loading,
      ios_semver: iosSemver, android_semver: androidSemver, other_semver: otherSemver,
    } = this.state;
    const isEdit = !!(defaultValue && defaultValue.feature_variant_id);
    return (
      <Modal
        open={opened}
        trigger={(
          <Button
            onClick={() => this.setState({ opened: true })}
            content={buttonLabel || 'Add New Variant'}
            color="green"
            icon={buttonIcon || 'plus'}
            labelPosition="left"
            floated="right"
          />
)}
      >
        <Header content={isEdit ? 'Edit Feature' : 'Add a Feature'} />
        <Modal.Content>
          {error
            && (
            <Message error>
              Unable to save feature variant:
              <pre>{JSON.stringify(error, null, '\t')}</pre>
            </Message>
            )
          }
          <p>
            A variant is a specialization of a feature. For example, the different choices for
            background color, e.g. &quot;white&quot;, &quot;red&quot;, &quot;blue&quot;,
            &quot;green&quot;, etc. A special type of variant, the control variant represents
            the default behavior for a feature. The control variant is never returned to the
            caller since the implication is &quot;do the default thing.&quot;
          </p>
          <p>
            Barring any other limitations on assignment of a feature (not currently supported in
            this UI), users will be assigned randomly based on point values. So if the points of
            all variants add up to 100, then points means &quot;percentage of the time this variant
            is assigned.&quot;
          </p>
          <Form loading={loading}>
            <Form.Input
              label="Name"
              name="name"
              value={name}
              onChange={(e, { value }) => this.onChange('name', value)}
              placeholder="Variant Name for app (keep it short)"
            />
            {!ignorePoints && (
            <Form.Input
              type="number"
              label="Points"
              value={points}
              onChange={(e, { value }) => this.onChange('points', value)}
              placeholder="10"
            />
            )}
            <Form.Input
              label="Remaining Assignments"
              value={remain}
              onChange={(e, { value }) => this.onChange('remaining_assignments', value)}
              placeholder={isEdit ? 'no change' : 'not set'}
            />
            <Form.Input
              type="number"
              label="Desirability (higher is more desirable)"
              value={desirability}
              onChange={(e, { value }) => this.onChange('desirability', value)}
              placeholder="0"
            />
            <Form.Group widths="equal">
              <Form.Input
                label="iOS SemVer"
                value={iosSemver}
                onChange={(e, { value }) => this.onChange('ios_semver', value)}
                placeholder="The caller must match this semver on iOS"
              />
              <Form.Input
                label="Android SemVer"
                value={androidSemver}
                onChange={(e, { value }) => this.onChange('android_semver', value)}
                placeholder="The caller must match this semver on Android"
              />
              <Form.Input
                label="Other SemVer"
                value={otherSemver}
                onChange={(e, { value }) => this.onChange('other_semver', value)}
                placeholder="The caller must match this semver on non-iOS/Android"
              />
            </Form.Group>
            <Popup
              wide
              trigger={(
                <Form.Checkbox
                  label="Control Variant"
                  checked={!!isControl}
                  onChange={(e, { checked }) => this.setState({ isControl: checked })}
                />
)}
              content={(
                <div>
                  <p>
                    The control variant is not returned to the caller. Therefore, this
                    variant should match the default behavior of the app. Additionally,
                    apps will not log the control variant to analytics, so if you need
                    the default behavior to go to analytics (e.g. for funnels to work),
                    then you should not have a control variant.
                  </p>
                </div>
)}
            />
            <Form.TextArea
              label="Payload (JSON)"
              value={payload}
              onChange={(e, { value }) => this.setState({ payload: value })}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button basic color="grey" onClick={() => this.setState({ opened: false })}>
            <Icon name="minus" />
            {' '}
Cancel
          </Button>
          <Button basic color="red" onClick={this.saveVariant}>
            <Icon name="plus" />
            {' '}
            {buttonLabel || 'Add New Variant'}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

AddFeatureVariant.propTypes = {
  fetch: PT.func.isRequired,
  featureId: PT.number.isRequired,
  buttonLabel: PT.string,
  buttonIcon: PT.string,
  defaultValue: PT.shape({
    feature_variant_id: PT.number,
  }),
  ignorePoints: PT.bool,
};

AddFeatureVariant.defaultProps = {
  defaultValue: null,
  buttonLabel: null,
  buttonIcon: null,
  ignorePoints: false,
};
